/**
 * 道具图描述生成核心。
 *
 * 这个模块只关心“节点语义”和“节点之间的输入/流程关系”，不关心这些关系来自编辑器图
 * 还是运行时图。编辑器版 `PropGraph` 和游戏端使用的 `PropRuntimeGraph` 都应先转换为
 * `PropDescriptionGraph`，再调用这里的描述生成函数。
 *
 * 设计目标：
 * - 保证编辑器和游戏端使用同一套中文描述规则。
 * - 避免游戏端依赖编辑器画布结构、强度算法、模拟算法等重逻辑。
 * - 保持描述算法可测试：同一份规范化图数据应稳定生成同一段文本。
 */

export type PropDescriptionDictItem = {
  value: number | string;
  label?: string;
};

export type PropDescriptionOptions = {
  types?: PropDescriptionDictItem[];
  rarities?: PropDescriptionDictItem[];
  tags?: PropDescriptionDictItem[];
  props?: Array<{ _id?: unknown; name?: string; assetId?: string }>;
  propType?: unknown;
};

export type PropDescriptionNode = {
  id: string;
  type: string;
  data?: Record<string, unknown>;
};

export type PropDescriptionEndpoint = {
  nodeId: string;
  portId: string;
};

export type PropDescriptionFlow = {
  id?: string;
  from: PropDescriptionEndpoint;
  to: PropDescriptionEndpoint;
  order?: number;
};

export type PropDescriptionBinding = {
  id?: string;
  source: PropDescriptionEndpoint;
  target: PropDescriptionEndpoint;
};

export type PropDescriptionGraph = {
  entryNodeIds: string[];
  nodes: Record<string, PropDescriptionNode>;
  flows: PropDescriptionFlow[];
  bindings: PropDescriptionBinding[];
};

type DescriptionContext = {
  nodes: PropDescriptionNode[];
  nodeMap: Map<string, PropDescriptionNode>;
  flowMap: Map<string, PropDescriptionFlow[]>;
  bindingMap: Map<string, PropDescriptionBinding[]>;
  options: PropDescriptionOptions;
  visited: Set<string>;
};

const attributeLabels: Record<string, string> = {
  rarity: "品质",
  tag: "标签",
  yield: "产出",
};

const compareLabels: Record<string, string> = {
  eq: "为",
  ne: "不等于",
  gt: "大于",
  gte: "大于等于",
  lt: "小于",
  lte: "小于等于",
};

const operationLabels: Record<string, string> = {
  add: "+",
  subtract: "-",
  multiply: "×",
  divide: "÷",
  mod: "取余",
};

const roundModeLabels: Record<string, string> = {
  round: "四舍五入",
  ceil: "向上取整",
  floor: "向下取整",
};

const relativePositionLabels: Record<string, string> = {
  adjacent: "相邻",
  sameRow: "同行",
  sameColumn: "同列",
  diagonal: "对角线",
};

const absolutePositionLabels: Record<string, string> = {
  corner: "角落",
  edge: "边缘",
  firstRow: "首行",
  lastRow: "尾行",
  firstColumn: "首列",
  lastColumn: "尾列",
  middleColumn: "中间列",
};

const typeLabels: Record<number, string> = {
  1: "原料",
  2: "辅料",
  3: "配方",
};

/**
 * 根据规范化图生成完整描述。
 *
 * 入口节点优先使用 `entryNodeIds`；如果入口为空，则自动扫描事件节点，保证旧数据或手工构造数据
 * 仍能生成尽可能完整的描述。
 */
export function getPropDescription(
  graph: PropDescriptionGraph,
  options: PropDescriptionOptions = {},
) {
  const ctx = createDescriptionContext(graph, options);
  const entries = graph.entryNodeIds
    .map((id) => ctx.nodeMap.get(id))
    .filter((node): node is PropDescriptionNode => Boolean(node));
  const eventNodes = ctx.nodes.filter(isEventNode);
  const descriptions = (entries.length ? entries : eventNodes)
    .map((node) => describeFlowFromNode(node, ctx))
    .filter(Boolean);
  return descriptions.length ? descriptions.map(ensureSentence).join("") : "";
}

/**
 * 返回负面效果片段，用于 UI 中对描述文本做高亮。
 *
 * 负面效果只由动作节点上的 `negative=true` 决定，不根据文本含义猜测。
 */
export function getPropNegativeDescriptionFragments(
  graph: PropDescriptionGraph,
  options: PropDescriptionOptions = {},
) {
  const ctx = createDescriptionContext(graph, options);
  const result: string[] = [];
  ctx.nodes.forEach((node) => {
    if (!isNegativeEffectNode(node)) return;
    const text = describeAction(node.type, node.id, ctx).trim();
    if (text) result.push(text, formatFailedActionFlowText(text));
  });
  return unique(result.filter(Boolean)).sort((a, b) => b.length - a.length);
}

function createDescriptionContext(
  graph: PropDescriptionGraph,
  options: PropDescriptionOptions,
): DescriptionContext {
  const nodes = objectValues(graph.nodes ?? {});
  const flowMap = new Map<string, PropDescriptionFlow[]>();
  (graph.flows ?? []).forEach((flow) => {
    flowMap.set(flow.from.nodeId, (flowMap.get(flow.from.nodeId) ?? []).concat(flow));
  });
  flowMap.forEach((flows) => flows.sort(compareFlow));

  const bindingMap = new Map<string, PropDescriptionBinding[]>();
  (graph.bindings ?? []).forEach((binding) => {
    const key = `${binding.target.nodeId}:${binding.target.portId}`;
    bindingMap.set(key, (bindingMap.get(key) ?? []).concat(binding));
  });

  return {
    nodes,
    nodeMap: new Map(nodes.map((node) => [node.id, node])),
    flowMap,
    bindingMap,
    options,
    visited: new Set(),
  };
}

function compareFlow(a: PropDescriptionFlow, b: PropDescriptionFlow) {
  const orderA = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
  const orderB = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
  if (orderA !== orderB) return orderA - orderB;
  return String(a.id ?? "").localeCompare(String(b.id ?? ""));
}

function isEventNode(node: PropDescriptionNode) {
  return (
    node.type === "eventTurnStart" ||
    node.type === "eventAcquire" ||
    node.type === "eventUse" ||
    node.type === "eventEliminated"
  );
}

function isActionNode(node: PropDescriptionNode) {
  return (
    node.type === "actionGrow" ||
    node.type === "actionExtraYield" ||
    node.type === "actionPurify" ||
    node.type === "actionProtect" ||
    node.type === "actionAccelerate" ||
    node.type === "actionTransform" ||
    node.type === "actionSpawn" ||
    node.type === "actionEliminate" ||
    node.type === "actionCollect" ||
    node.type === "actionHarvest" ||
    node.type === "actionRarityWeight" ||
    node.type === "actionExtendCommission" ||
    node.type === "actionSwapPosition" ||
    node.type === "actionPermanentBoost" ||
    node.type === "actionTriggerVisual"
  );
}

function isNegativeEffectNode(node: PropDescriptionNode) {
  return isActionNode(node) && node.data?.negative === true;
}

function describeFlowFromNode(node: PropDescriptionNode, ctx: DescriptionContext, actionChainDepth = 0): string {
  if (ctx.visited.has(node.id)) return "";
  ctx.visited.add(node.id);
  const currentActionChainDepth = isActionNode(node) ? Math.max(1, actionChainDepth) : actionChainDepth;

  switch (node.type) {
    case "eventTurnStart":
      return describeTurnStartFlow(node.data ?? {}, node.id, ctx);
    case "eventAcquire":
      return describeAcquireFlow(node.id, ctx);
    case "eventUse":
      return describeUseFlow(node.id, ctx);
    case "eventEliminated":
      return describeEliminatedFlow(node.id, ctx);
    case "actionGrow":
    case "actionExtraYield":
    case "actionPurify":
    case "actionProtect":
    case "actionAccelerate":
    case "actionTransform":
    case "actionSpawn":
    case "actionEliminate":
    case "actionCollect":
    case "actionHarvest":
    case "actionRarityWeight":
    case "actionExtendCommission":
    case "actionSwapPosition":
    case "actionPermanentBoost":
      return joinClause([describeAction(node.type, node.id, ctx), describeNextFlows(node.id, ctx, currentActionChainDepth)]);
    case "actionTriggerVisual":
      return describeNextFlows(node.id, ctx, currentActionChainDepth);
    default: {
      const next = describeNextFlows(node.id, ctx);
      return next || describeValueOutput(node, ctx);
    }
  }
}

function isTransparentVisualOnlyFlow(nodeId: string, ctx: DescriptionContext) {
  const flows = ctx.flowMap.get(nodeId) ?? [];
  if (!flows.length) return false;
  return flows.every((flow) => {
    const node = ctx.nodeMap.get(flow.to.nodeId);
    if (node?.type !== "actionTriggerVisual") return false;
    const branchCtx: DescriptionContext = { ...ctx, visited: new Set(ctx.visited) };
    return !describeNextFlows(node.id, branchCtx).trim();
  });
}

function describeTurnStartFlow(data: Record<string, unknown>, nodeId: string, ctx: DescriptionContext) {
  const next = describeNextFlows(nodeId, ctx);
  const period = Math.max(0, Number(data.interval ?? 0)) + 1;
  const timing = period <= 1 ? "每轮" : `每${period}轮`;
  const loopCount = Number(data.loop ?? 0);
  const loop = loopCount > 0 ? `循环${data.loop}次` : "";
  if (!next && isTransparentVisualOnlyFlow(nodeId, ctx)) return "";
  if (!next) return joinClause([`${timing}开始`, loop]);
  if (loopCount === 1 && period > 1 && isDelayedOneShotText(next)) return `${period}轮后${next}`;
  if (!loop && period <= 1 && shouldPrefixEveryTurn(ctx) && !isConditionLeadText(next)) return formatPassiveEveryTurnDescription(next);
  if (!loop && next.startsWith("从")) return `${timing}${next}`;
  if (!loop && period <= 1 && isImplicitEveryTurnText(next)) return next;
  if (!loop) {
    const probabilityAction = stripLeadingProbabilityActionPrefix(next);
    if (probabilityAction) return period <= 1 ? probabilityAction : `${timing}${probabilityAction}`;
  }
  if (!loop && isPeriodicDirectActionText(next)) return `${timing}${next}`;
  return joinClause([`${timing}开始`, loop, next]);
}

function shouldPrefixEveryTurn(ctx: DescriptionContext) {
  return Number(ctx.options.propType) === 3;
}

function isConditionLeadText(text: string) {
  return /^(同时有|有|场上有|位于|没有).+时/.test(text);
}

function formatPassiveEveryTurnDescription(text: string) {
  return text
    .split("。")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const formatted = formatPassiveEveryTurnPart(part);
      if (formatted.startsWith("每轮")) return formatted;
      return shouldKeepEveryTurnPrefix(formatted) ? `每轮${formatted}` : formatted;
    })
    .join("。");
}

function formatPassiveEveryTurnPart(text: string) {
  return text.replace(/^使(?=.+魔力)/, "");
}

function shouldKeepEveryTurnPrefix(text: string) {
  if (text.startsWith("消除") || text.startsWith("生成")) return true;
  if (text.includes("概率") || text.startsWith("每有") || text.includes("种类数")) return false;
  return isPermanentYieldText(text);
}

function stripLeadingProbabilityActionPrefix(text: string) {
  const match = text.match(/^以?(.+?概率)(.+)$/);
  return match ? `${match[1]}${match[2]}` : "";
}

function describeAcquireFlow(nodeId: string, ctx: DescriptionContext) {
  const next = describeNextFlows(nodeId, ctx);
  if (!next && isTransparentVisualOnlyFlow(nodeId, ctx)) return "";
  return next ? `持有时${next}` : "持有时触发";
}

function describeUseFlow(nodeId: string, ctx: DescriptionContext) {
  const next = describeNextFlows(nodeId, ctx);
  if (!next && isTransparentVisualOnlyFlow(nodeId, ctx)) return "";
  return next ? formatUseEventFlowText(next) : "使用后触发";
}

function formatUseEventFlowText(text: string) {
  const formatted = formatUseFlowText(text);
  const simpleText = formatSimpleUseFlowText(formatted);
  return `使用后${simpleText || formatted}`;
}

function formatSimpleUseFlowText(text: string) {
  if (isSimpleUseFlowText(text)) return text;
  const parts = text.split("；").map((part) => part.trim()).filter(Boolean);
  if (parts.length <= 1) return "";
  return parts.every(isSimpleUseFlowText) ? parts.join("，") : "";
}

function isSimpleUseFlowText(text: string) {
  if (/[，；]/.test(text)) return false;
  if (/^(有|位于|场上有|\d+(?:\.\d+)?%概率)/.test(text)) return false;
  if (text.indexOf("之后") >= 0 || text.indexOf("否则") >= 0) return false;
  if (text.indexOf("倒计时") >= 0) return true;
  return (
    text.startsWith("生成") ||
    text.startsWith("消除") ||
    text.startsWith("额外产出") ||
    isMagicYieldText(text) ||
    text.startsWith("永久增加") ||
    text.startsWith("永久减少") ||
    isPermanentYieldText(text) ||
    text.startsWith("倒计时") ||
    text.startsWith("净化") ||
    text.startsWith("保护") ||
    text.startsWith("免疫")
  );
}

function formatUseFlowText(text: string) {
  const formatted = text
    .replace(/(^|[，；])使(随机(?:\d+个)?[^，；]+?)额外产出/g, "$1$2产出")
    .replace(/(^|[，；])(随机(?:\d+个)?[^，；]+?)额外产出/g, "$1$2产出")
    .replace(/(^|[，；])使(随机(?:\d+个)?[^，；]+?倒计时)/g, "$1$2")
    .replace(/(^|[，；])使([^，；]+?倒计时)/g, "$1$2")
    .replace(/(^|[，；])随机(-?\d+(?:\.\d+)?~-?\d+(?:\.\d+)?个[^，；]+?魔力永久[+-])/g, (_match, prefix, body) => `${prefix}${body}`)
    .replace(/(^|[，；])随机(?!\d+个)([^，；]+?魔力永久[+-])/g, (_match, prefix, body) => `${prefix}1个${body}`);
  return collapseConsecutiveUseSpawnText(formatted);
}

function collapseConsecutiveUseSpawnText(text: string) {
  let result = text;
  let next = result.replace(/(^|[，；])生成([^，；]+)，生成/g, "$1生成$2，");
  while (next !== result) {
    result = next;
    next = result.replace(/(^|[，；])生成([^，；]+)，生成/g, "$1生成$2，");
  }
  return result;
}

function describeEliminatedFlow(nodeId: string, ctx: DescriptionContext) {
  const target = describeInputTarget(nodeId, "target", ctx);
  const next = describeNextFlows(nodeId, ctx);
  const timing = target === "当前道具" ? "被消除" : `${target}被消除`;
  if (!next && isTransparentVisualOnlyFlow(nodeId, ctx)) return "";
  if (!next) return `${timing}时触发`;
  return `${timing}后${formatEliminatedFlowText(next)}`;
}

function formatEliminatedFlowText(text: string) {
  return text.replace(/^使(?=.+(?:永久(?:增加|减少)|魔力永久[+-]))/, "");
}

function describeNextFlows(nodeId: string, ctx: DescriptionContext, actionChainDepth = 0) {
  const sourceNode = ctx.nodeMap.get(nodeId);
  const flows = ctx.flowMap.get(nodeId) ?? [];
  const hasCompleteOutput = flows.some((flow) => {
    if (flow.from.portId !== "complete") return false;
    const node = ctx.nodeMap.get(flow.to.nodeId);
    if (!node) return false;
    const branchCtx: DescriptionContext = { ...ctx, visited: new Set(ctx.visited) };
    const nextActionChainDepth = isActionNode(node) ? actionChainDepth + 1 : actionChainDepth;
    return Boolean(describeFlowFromNode(node, branchCtx, nextActionChainDepth).trim());
  });
  const hasSourceConditionOutput =
    sourceNode && isActionNode(sourceNode) ? Boolean(describeActionConditionPrefix(sourceNode.id, ctx).trim()) : false;
  const parts = flows
    .map((flow) => {
      const node = ctx.nodeMap.get(flow.to.nodeId);
      if (!node) return "";
      const nextActionChainDepth =
        isActionNode(node) ? (flow.from.portId === "complete" ? actionChainDepth + 1 : 1) : actionChainDepth;
      const targetHasCompleteOutput = isActionNode(node) ? hasDescribedCompleteOutput(node.id, ctx, nextActionChainDepth) : false;
      const text = describeFlowFromNode(node, ctx, nextActionChainDepth);
      if (sourceNode?.type === "actionTriggerVisual" && isActionNode(node)) return text;
      if (flow.from.portId === "complete" && isActionNode(node)) {
        return prefixCompletedActionFlow(text, sourceNode, node, nextActionChainDepth, targetHasCompleteOutput && shouldUseContinueCompletedActionPrefix(node));
      }
      if (flow.from.portId === "fail" && isActionNode(node)) {
        return prefixFailedActionFlow(text, !hasCompleteOutput && !hasSourceConditionOutput, sourceNode, node, ctx);
      }
      if (flow.from.portId === "each" && sourceNode?.type === "actionEliminate" && isActionNode(node)) {
        return prefixEliminateEachActionFlow(text);
      }
      if (flow.from.portId === "each" && sourceNode && isActionNode(sourceNode) && isActionNode(node)) {
        return prefixEachActionFlow(text);
      }
      return text;
    })
    .filter(Boolean);
  return joinNextFlowTexts(parts, sourceNode);
}

function hasDescribedCompleteOutput(nodeId: string, ctx: DescriptionContext, actionChainDepth: number) {
  return (ctx.flowMap.get(nodeId) ?? []).some((flow) => {
    if (flow.from.portId !== "complete") return false;
    const node = ctx.nodeMap.get(flow.to.nodeId);
    if (!node) return false;
    const branchCtx: DescriptionContext = { ...ctx, visited: new Set(ctx.visited) };
    const nextActionChainDepth = isActionNode(node) ? actionChainDepth + 1 : actionChainDepth;
    return Boolean(describeFlowFromNode(node, branchCtx, nextActionChainDepth).trim());
  });
}

function joinNextFlowTexts(parts: string[], _sourceNode: PropDescriptionNode | undefined) {
  return parts.join("，");
}

function prefixCompletedActionFlow(
  text: string,
  _sourceNode: PropDescriptionNode | undefined,
  _nextNode?: PropDescriptionNode,
  _actionChainDepth = 2,
  useContinuePrefix = false,
) {
  const trimmed = text.trim();
  if (!trimmed) return "";
  if (useContinuePrefix) return `再${formatCompletedActionFlowText(trimmed)}`;
  return `成功则${formatCompletedActionFlowText(trimmed)}`;
}

function shouldUseContinueCompletedActionPrefix(node: PropDescriptionNode) {
  return !["actionGrow", "actionExtraYield", "actionAccelerate", "actionPermanentBoost"].includes(node.type);
}

function formatCompletedActionFlowText(text: string) {
  return stripSelfActionSubject(text.trim().replace(/^(并|再)/, "").replace(/^后，/, "").replace(/^之后/, ""));
}

function prefixFailedActionFlow(
  text: string,
  useFailurePrefix = false,
  sourceNode?: PropDescriptionNode,
  nextNode?: PropDescriptionNode,
  ctx?: DescriptionContext,
) {
  const trimmed = stripSelfActionSubject(text.trim().replace(/^并/, ""));
  if (!trimmed || trimmed.startsWith("否则") || trimmed.startsWith("失败则")) return trimmed;
  const prefix = useFailurePrefix ? "失败则" : "否则";
  const collapsed = describeCollapsedFailedMultiplierFlow(sourceNode, nextNode, ctx);
  if (collapsed) return `${prefix}${collapsed}`;
  return `${prefix}${formatFailedActionFlowText(stripRepeatedFailedActionTarget(trimmed, sourceNode, nextNode, ctx))}`;
}

function describeCollapsedFailedMultiplierFlow(
  sourceNode: PropDescriptionNode | undefined,
  nextNode: PropDescriptionNode | undefined,
  ctx: DescriptionContext | undefined,
) {
  if (!ctx || sourceNode?.type !== "actionExtraYield" || nextNode?.type !== "actionExtraYield") return "";
  if (describeInputTargets(sourceNode.id, ctx) !== describeInputTargets(nextNode.id, ctx)) return "";
  const amount = describeOptionalInputValue(nextNode.id, "amount", ctx);
  const multiplier = describeOptionalInputValue(nextNode.id, "multiplier", ctx);
  if (amount !== undefined || multiplier === undefined) return "";
  return describeFailedMultiplierEffect(multiplier);
}

function describeFailedMultiplierEffect(multiplier: unknown) {
  const numeric = Number(formatValue(multiplier));
  if (Number.isFinite(numeric)) {
    const value = numeric + 1;
    return value < 0 ? `x(${formatValue(value)})` : `x${formatValue(value)}`;
  }
  return `x(1+${formatValue(multiplier)})`;
}

function stripRepeatedFailedActionTarget(
  text: string,
  sourceNode: PropDescriptionNode | undefined,
  nextNode: PropDescriptionNode | undefined,
  ctx: DescriptionContext | undefined,
) {
  if (!ctx || sourceNode?.type !== "actionExtraYield" || nextNode?.type !== "actionExtraYield") return text;
  const sourceTarget = describeInputTargets(sourceNode.id, ctx);
  const nextTarget = describeInputTargets(nextNode.id, ctx);
  if (!sourceTarget || sourceTarget !== nextTarget) return text;
  const magicPrefix = `${nextTarget}魔力`;
  if (text.startsWith(magicPrefix)) return text.slice(magicPrefix.length);
  if (text.startsWith(nextTarget)) return text.slice(nextTarget.length);
  return text;
}

function formatFailedActionFlowText(text: string) {
  return stripSelfActionSubject(text.replace(/^消除随机(.+)$/, "随机消除$1"));
}

function prefixEliminateEachActionFlow(text: string) {
  return prefixEachActionFlow(text);
}

function prefixEachActionFlow(text: string) {
  const trimmed = formatEachActionFlowText(stripSelfActionSubject(text.trim().replace(/^并/, "")));
  if (!trimmed || trimmed.startsWith("每个")) return trimmed;
  if (/^\d+(?:\.\d+)?%概率/.test(trimmed)) return `每个${trimmed}`;
  if (trimmed.startsWith("使") || trimmed.startsWith("将")) return `每个${trimmed}`;
  return `每个使${trimmed}`;
}

function formatEachActionFlowText(text: string) {
  return text.replace(/生成1个/g, "生成");
}

function describeAction(type: string, nodeId: string, ctx: DescriptionContext) {
  const target = describeInputTargets(nodeId, ctx);
  const amount = describeInputValue(nodeId, "amount", ctx, "未设置");
  const targetPrefix = describeGrowTargetPrefix(target, nodeId, ctx);
  const conditionPrefix = describeActionConditionPrefix(nodeId, ctx);

  if (type === "actionTriggerVisual") return "";
  if (type === "actionAccelerate") return joinConditionedActionText(conditionPrefix, `${describeAccelerateTargetPrefix(target, nodeId, ctx)}倒计时${formatSignedValue(amount)}`);
  if (type === "actionExtraYield") return describeExtraYieldAction(nodeId, describeExtraYieldTargetPrefix(target, nodeId, ctx), conditionPrefix, ctx);
  if (type === "actionPurify") {
    if (target === "输入目标") return `${conditionPrefix}净化负面效果`;
    const purifiedTarget = target === "当前道具" ? "" : formatPurifyTarget(target.replace(/道具$/, ""));
    return `${conditionPrefix}净化${purifiedTarget}负面效果`;
  }
  if (type === "actionEliminate") {
    if (target === "当前道具") return `${conditionPrefix}消除自身`;
    const optionalAmount = describeOptionalInputValue(nodeId, "amount", ctx);
    if (optionalAmount && isEliminateCountFromTargetQuantity(nodeId, ctx)) return `${conditionPrefix}消除${target}`;
    return `${conditionPrefix}消除${formatEliminateTarget(optionalAmount, target)}`;
  }
  if (type === "actionProtect") {
    if (target === "当前道具") return `${conditionPrefix}免疫消除`;
    return `${conditionPrefix}保护${target}不被消除`;
  }
  if (type === "actionTransform") {
    const prototype = formatTransformPrototypeText(describeInputPrototypes(nodeId, ctx), nodeId, ctx);
    const transformTarget = describeTransformTarget(nodeId, target, ctx);
    if (transformTarget) return formatTransformActionText(transformTarget, prototype, conditionPrefix);
    if (target === "输入目标" || target === "当前道具") return `${conditionPrefix}转化为${prototype}`;
    if (isDirectTransformTargetText(target)) return formatTransformActionText(target, prototype, conditionPrefix);
    if (conditionPrefix) return formatTransformActionText(target, prototype, conditionPrefix);
    return `转化${target}为${prototype}`;
  }
  if (type === "actionSpawn") {
    const spawnTarget = describeInputSpawnTargets(nodeId, ctx);
    const position = describeInputTarget(nodeId, "position", ctx, "输入位置");
    const positionPrefix = position === "输入位置" || position === "当前道具" ? "" : `在${position}`;
    const groupedSpawnAction = describeGroupedQuantitySpawnAction(nodeId, conditionPrefix, positionPrefix, spawnTarget, ctx);
    if (groupedSpawnAction) return groupedSpawnAction;
    return `${conditionPrefix}${positionPrefix}生成${formatCountedTarget(amount, spawnTarget)}`;
  }
  if (type === "actionCollect") return `${conditionPrefix}从${describeCollectTarget(target)}收集${formatValue(amount)}点魔力`;
  if (type === "actionHarvest") return `${conditionPrefix}获得收集的魔力`;
  if (type === "actionRarityWeight") {
    const rarity = describeInputConfiguredValues(nodeId, "rarity", ctx, "品质");
    const multiplier = describeInputValue(nodeId, "multiplier", ctx, 1);
    return `${conditionPrefix}${rarity}物品抽取概率${describeRarityWeightChange(multiplier)}`;
  }
  if (type === "actionExtendCommission") return `${conditionPrefix}调和次数+1，每份委托限用1次`;
  if (type === "actionSwapPosition") {
    const swapText = describeSwapPositionAction(nodeId, target, ctx);
    if (swapText) return `${conditionPrefix}${swapText}`;
    const position = describeSwapPositionDestination(nodeId, ctx, "输入位置");
    return `${conditionPrefix}将${target}调换到${position}`;
  }
  if (type === "actionPermanentBoost") return describePermanentBoostAction(nodeId, target, conditionPrefix, ctx);
  const groupedGrowAction = describeGroupedQuantityGrowAction(nodeId, targetPrefix, conditionPrefix, ctx);
  if (groupedGrowAction) return groupedGrowAction;
  return joinConditionedActionText(conditionPrefix, `${targetPrefix}${describeGrowAmountEffect(amount)}`);
}

function describePermanentBoostAction(nodeId: string, target: string, conditionPrefix: string, ctx: DescriptionContext) {
  const node = ctx.nodeMap.get(nodeId);
  const amount = describeOptionalInputValue(nodeId, "amount", ctx);
  const multiplier = describeOptionalInputValue(nodeId, "multiplier", ctx);
  const effects = [
    amount === undefined ? undefined : describePermanentBoostAmountEffect(amount),
    multiplier === undefined ? undefined : describePermanentBoostMultiplierEffect(multiplier),
  ].filter((item): item is PermanentBoostEffectText => Boolean(item));
  const effectText = formatPermanentBoostEffectList(effects);
  const targetText = describePermanentBoostTargetText(target, getInputSourceNode(nodeId, "targets", ctx));
  const mutexTag = String(node?.data?.mutexTag ?? "").trim();
  const mutexText = mutexTag && mutexTag !== "default" ? `${mutexTag}效果不叠加` : "同类效果不叠加";
  const targetPrefix = formatConditionedTargetPrefix(conditionPrefix, targetText);
  if (amount !== undefined && multiplier === undefined) {
    return `${targetPrefix}${describePermanentBoostMagicEffect(amount)}，持续生效，${mutexText}`;
  }
  if (amount === undefined && multiplier !== undefined) {
    return `${targetPrefix}${describePermanentBoostMultiplierMagicEffect(multiplier)}，持续生效，${mutexText}`;
  }
  return `${targetPrefix}产出${effectText}，持续生效，${mutexText}`;
}

type PermanentBoostEffectText = {
  verb: "增加" | "减少" | "不变";
  value: string;
};

function describePermanentBoostTargetText(target: string, source: PropDescriptionNode | undefined) {
  if (target === "当前道具") return "";
  if (target === "输入目标") return "目标";
  if (source?.type === "prototypeLibrary") return describePrototypeLibraryType(source);
  return target.replace(/^使/, "").replace(/^随机/, "").replace(/道具$/, "").replace(/库$/, "");
}

function describePermanentBoostAmountEffect(amount: unknown): PermanentBoostEffectText {
  const numeric = Number(formatValue(amount));
  if (Number.isFinite(numeric) && numeric < 0) return { verb: "减少", value: `${formatValue(Math.abs(numeric))}点魔力` };
  return { verb: "增加", value: `${formatValue(amount)}点魔力` };
}

function describePermanentBoostMagicEffect(amount: unknown) {
  const text = formatValue(amount);
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return `魔力${numeric >= 0 ? "+" : "-"}${formatValue(Math.abs(numeric))}`;
  return `魔力+${text}`;
}

function describePermanentBoostMultiplierMagicEffect(multiplier: unknown) {
  return `魔力x${formatValue(multiplier)}`;
}

function describePermanentBoostMultiplierEffect(multiplier: unknown): PermanentBoostEffectText {
  const numeric = Number(formatValue(multiplier));
  if (!Number.isFinite(numeric)) return { verb: "增加", value: `${formatValue(multiplier)}倍` };
  const percent = (numeric - 1) * 100;
  if (percent > 0) return { verb: "增加", value: `${formatValue(percent)}%` };
  if (percent < 0) return { verb: "减少", value: `${formatValue(Math.abs(percent))}%` };
  return { verb: "不变", value: "" };
}

function formatPermanentBoostEffectList(effects: PermanentBoostEffectText[]) {
  const validEffects = effects.filter((item) => item.verb !== "不变" && item.value);
  if (!validEffects.length) return "不变";
  const firstVerb = validEffects[0].verb;
  if (validEffects.every((item) => item.verb === firstVerb)) {
    return `${firstVerb}${validEffects.map((item) => item.value).join("、")}`;
  }
  return validEffects.map((item) => `${item.verb}${item.value}`).join("、");
}

function describeCollectTarget(target: string) {
  if (target === "盘面原料" || target === "盘面道具") return "原料各";
  if (target.startsWith("场上每个")) return target.replace(/^场上每个/, "每个").replace(/道具$/, "");
  if (target.startsWith("场上")) return `${target.slice(2).replace(/道具$/, "")}各`;
  return target;
}

function describeSwapPositionAction(nodeId: string, target: string, ctx: DescriptionContext) {
  const positionSource = getInputSourceNode(nodeId, "positions", ctx);
  const randomBoardPosition = describeRandomBoardPositionSwapTarget(positionSource, ctx);
  if (target !== "当前道具" && randomBoardPosition) {
    const singleTarget = formatSingleRandomSwapTarget(target);
    return singleTarget ? `交换${singleTarget}到${randomBoardPosition}` : `将${target}交换到${randomBoardPosition}`;
  }
  if (target !== "当前道具") {
    const position = describeSwapPositionDestination(nodeId, ctx, "输入位置");
    const swapTarget = describeCountedSwapMoveTarget(nodeId, target, ctx);
    if (swapTarget && position && position !== "输入位置" && position !== "当前道具") return `交换${swapTarget}到${position}`;
    return "";
  }
  const randomPrototype = describeRandomPrototypeSwapTarget(positionSource, ctx);
  if (randomPrototype) return `和${randomPrototype}交换位置`;
  if (randomBoardPosition) return `交换到${randomBoardPosition}`;
  const position = describeSwapPositionDestination(nodeId, ctx, "输入位置");
  if (!position || position === "输入位置" || position === "当前道具") return "";
  return `和${normalizeSwapPositionTarget(position)}交换位置`;
}

function describeSwapPositionDestination(nodeId: string, ctx: DescriptionContext, fallback: string) {
  const source = getInputSourceNode(nodeId, "positions", ctx);
  if (source?.type === "targetBoard") return describeBoardMaterialPrefix(source) || describeBoardMaterials(source).replace(/原料$/, "");
  return describeInputTarget(nodeId, "positions", ctx, fallback);
}

function describeRandomBoardPositionSwapTarget(source: PropDescriptionNode | undefined, ctx: DescriptionContext) {
  if (source?.type !== "filterRandom") return "";
  const count = describeInputValue(source.id, "count", ctx, source.data?.count ?? 1);
  if (!isSingleCount(count)) return "";
  const boardSource = getInputSourceNode(source.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard") return "";
  return describeBoardMaterialPrefix(boardSource);
}

function describeRandomPrototypeSwapTarget(source: PropDescriptionNode | undefined, ctx: DescriptionContext) {
  if (source?.type !== "filterRandom") return "";
  const count = describeInputValue(source.id, "count", ctx, source.data?.count ?? 1);
  if (!isSingleCount(count)) return "";
  const prototypeFilter = getInputSourceNode(source.id, "targets", ctx);
  if (prototypeFilter?.type === "filterTag") {
    const boardSource = getInputSourceNode(prototypeFilter.id, "targets", ctx);
    if (boardSource?.type !== "targetBoard" || boardSource.data?.scope !== "all") return "";
    const tag = describeInputConfiguredValues(prototypeFilter.id, "tag", ctx, "标签");
    return tag && tag !== "标签" ? `1个${tag}` : "";
  }
  if (prototypeFilter?.type !== "filterPrototype") return "";
  const boardSource = getInputSourceNode(prototypeFilter.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard" || boardSource.data?.scope !== "all") return "";
  const prototype = describeInputPrototypeValues(prototypeFilter.id, "prototype", ctx, "指定原型");
  if (!prototype || prototype === "指定原型") return "";
  return `1个${prototype}`;
}

function formatSingleRandomSwapTarget(target: string) {
  const text = target.trim().replace(/^场上/, "");
  if (/^1个/.test(text)) return text;
  if (/^随机(?!\d+(?:\.\d+)?个)/.test(text)) return `1个${text.replace(/^随机/, "")}`;
  return "";
}

function describeCountedSwapMoveTarget(nodeId: string, target: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "targets", ctx);
  if (source?.type !== "filterRandom") return "";
  const text = target.trim().replace(/^场上/, "");
  return text.replace(/^随机/, "");
}

function normalizeSwapPositionTarget(text: string) {
  return text.replace(/^1个/, "随机");
}

function describeGrowTargetPrefix(target: string, nodeId?: string, ctx?: DescriptionContext) {
  if (target === "输入目标") return "";
  if (target === "当前道具") return "";
  if (target.startsWith("场上")) return target.slice(2);
  if (/^\d+(?:\.\d+)?个/.test(target)) return target;
  if (/^随机-?\d+(?:\.\d+)?~-?\d+(?:\.\d+)?个/.test(target)) return target.replace(/^随机/, "");
  if (isRandomTargetText(target)) return formatSingleRandomActionTarget(target);
  if (nodeId && ctx && isGlobalBoardTargetSource(getInputSourceNode(nodeId, "targets", ctx), ctx)) return target.replace(/道具$/, "");
  if (isPositionedTargetText(target)) return target;
  return `使${target}`;
}

function isPositionedTargetText(target: string) {
  return objectValues(relativePositionLabels).concat(objectValues(absolutePositionLabels)).some((label) => target.startsWith(label));
}

function describeTransformTarget(nodeId: string, fallback: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "targets", ctx);
  if (source?.type !== "filterPrototype") return "";
  const boardSource = getInputSourceNode(source.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard" || boardSource.data?.scope !== "relative") return "";
  const boardPrefix = describeBoardMaterialPrefix(boardSource);
  const prototype = describeInputPrototypeValues(source.id, "prototype", ctx, "指定原型");
  if (!boardPrefix || !prototype || prototype === "指定原型") return "";
  if (fallback.startsWith("随机")) return fallback;
  return `1个${boardPrefix}${prototype}`;
}

function formatTransformActionText(target: string, prototype: string, conditionPrefix: string) {
  const formattedTarget = formatTransformTargetText(target);
  if (!conditionPrefix) return `转化${formattedTarget}为${prototype}`;
  if (isProbabilityConditionText(conditionPrefix)) return `${formattedTarget}${conditionPrefix}转化为${prototype}`;
  return `${conditionPrefix}转化${formattedTarget}为${prototype}`;
}

function isDirectTransformTargetText(target: string) {
  return /^\d+(?:\.\d+)?个/.test(target) || isRandomTargetText(target);
}

function isProbabilityConditionText(text: string) {
  return text.indexOf("概率") >= 0;
}

function isStandaloneProbabilityConditionText(text: string) {
  return text.indexOf("概率") >= 0 && text.indexOf("时") < 0;
}

function formatTransformTargetText(target: string) {
  if (/^随机(?!\d+(?:\.\d+)?个)/.test(target)) return `1个${target.replace(/^随机/, "")}`;
  return target;
}

function formatTransformPrototypeText(text: string, nodeId: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "prototypes", ctx);
  if (!isPrototypeLibraryFilteredSource(source, ctx)) return text;
  return text.replace(/^随机/, "");
}

function isPrototypeLibraryFilteredSource(source: PropDescriptionNode | undefined, ctx: DescriptionContext): boolean {
  if (!source || (source.type !== "filterRarity" && source.type !== "filterTag" && source.type !== "filterRandom")) return false;
  const target = getInputSourceNode(source.id, "targets", ctx);
  return target?.type === "prototypeLibrary" || isPrototypeLibraryFilteredSource(target, ctx);
}

function describeExtraYieldTargetPrefix(target: string, nodeId?: string, ctx?: DescriptionContext) {
  if (target === "输入目标" || target === "当前道具") return "";
  if (target.startsWith("场上")) return target.slice(2);
  if (isRandomTargetText(target)) return target;
  if (nodeId && ctx && isGlobalBoardTargetSource(getInputSourceNode(nodeId, "targets", ctx), ctx)) return target.replace(/道具$/, "");
  return target;
}

function isRandomTargetText(target: string) {
  return target.startsWith("随机") || target.startsWith("场上随机") || target.indexOf("上的随机") >= 0 || target.indexOf("的随机") >= 0;
}

function describeExtraYieldAction(nodeId: string, targetPrefix: string, conditionPrefix: string, ctx: DescriptionContext) {
  const amount = describeOptionalInputValue(nodeId, "amount", ctx);
  const multiplier = describeOptionalInputValue(nodeId, "multiplier", ctx);
  const globalProbabilityText = describeGlobalTargetProbabilityExtraYield(nodeId, ctx);
  if (globalProbabilityText && amount && !multiplier) return `${globalProbabilityText}${describeExtraYieldAmountEffect(amount)}`;
  const groupedQuantityAddAction = describeGroupedQuantityAddExtraYieldAction(nodeId, targetPrefix, conditionPrefix, ctx);
  if (groupedQuantityAddAction && amount && !multiplier) return groupedQuantityAddAction;
  const groupedQuantityAction = describeGroupedQuantityExtraYieldAction(nodeId, targetPrefix, conditionPrefix, ctx);
  if (groupedQuantityAction && amount && !multiplier) return groupedQuantityAction;
  const perQuantityAmount = describePerQuantityExtraYieldAmount(nodeId, ctx);
  if (perQuantityAmount && amount && !multiplier) return `${conditionPrefix}${perQuantityAmount.text}${targetPrefix}${describeExtraYieldAmountEffect(perQuantityAmount.amount)}`;
  if (amount && multiplier) return `${formatConditionedTargetPrefix(conditionPrefix, targetPrefix)}${describeExtraYieldAmountEffect(amount)}，${describeExtraYieldMultiplierEffect(multiplier)}`;
  if (amount) return `${formatConditionedTargetPrefix(conditionPrefix, targetPrefix)}${describeExtraYieldAmountEffect(amount)}`;
  if (multiplier) return `${formatConditionedTargetPrefix(conditionPrefix, targetPrefix)}${describeExtraYieldMultiplierEffect(multiplier)}`;
  return `${conditionPrefix}${targetPrefix}额外产出`;
}

function formatConditionedTargetPrefix(conditionPrefix: string, targetPrefix: string) {
  if (isStandaloneProbabilityConditionText(conditionPrefix) && targetPrefix) return `${targetPrefix}${conditionPrefix}`;
  return `${conditionPrefix}${targetPrefix}`;
}

function describeGlobalTargetProbabilityExtraYield(nodeId: string, ctx: DescriptionContext) {
  const target = describeGlobalTaggedTarget(nodeId, ctx);
  const probability = describeSingleProbabilityCondition(nodeId, ctx);
  if (!target || !probability) return "";
  return `${target}${probability}`;
}

function describeGlobalTaggedTarget(nodeId: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "targets", ctx);
  if (source?.type !== "filterTag") return "";
  const boardSource = getInputSourceNode(source.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard" || boardSource.data?.scope !== "all") return "";
  return describeInputConfiguredValues(source.id, "tag", ctx, "标签");
}

function describeSingleProbabilityCondition(nodeId: string, ctx: DescriptionContext) {
  const sources = getBindings(nodeId, "condition", ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropDescriptionNode => Boolean(node));
  if (sources.length !== 1 || sources[0].type !== "logicProbability") return "";
  return `${describeProbabilityValue(sources[0].data?.probability)}概率`;
}

function describePerQuantityExtraYieldAmount(nodeId: string, ctx: DescriptionContext): { text: string; amount: unknown } | undefined {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const detail = amountSource ? getQuantityMultiplyAmountDescription(amountSource, ctx) : undefined;
  return detail ? { text: detail.text, amount: detail.amount } : undefined;
}

function describeGroupedQuantityExtraYieldAction(nodeId: string, targetPrefix: string, conditionPrefix: string, ctx: DescriptionContext) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const grouped = amountSource ? getGroupedQuantityAmountDescription(amountSource, ctx, { includeGlobalPrefix: false }) : "";
  if (!grouped) return "";
  return `${conditionPrefix}${grouped}${targetPrefix}${describeExtraYieldAmountEffect(1)}`;
}

function describeGroupedQuantityAddExtraYieldAction(nodeId: string, targetPrefix: string, conditionPrefix: string, ctx: DescriptionContext) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const detail = amountSource ? getGroupedQuantityAddAmountDescription(amountSource, ctx) : undefined;
  if (!detail) return "";
  return `${conditionPrefix}${detail.text}${targetPrefix}${describeExtraYieldAmountEffect(detail.amount)}`;
}

function getGroupedQuantityAddAmountDescription(source: PropDescriptionNode, ctx: DescriptionContext): { text: string; amount: unknown } | undefined {
  if (source.type !== "intOperation" && source.type !== "floatOperation") return undefined;
  if (source.data?.op !== "add") return undefined;
  const left = getInputSourceNode(source.id, "left", ctx);
  const right = getInputSourceNode(source.id, "right", ctx);
  const grouped = left ? getGroupedQuantityAmountDescription(left, ctx, { includeGlobalPrefix: true }) : "";
  const amount = getNumericConstantValue(right);
  if (!grouped || amount === undefined) return undefined;
  return { text: grouped, amount };
}

function getQuantityMultiplyAmountDescription(source: PropDescriptionNode, ctx: DescriptionContext): { text: string; amount: unknown } | undefined {
  if (source.type !== "intOperation" && source.type !== "floatOperation") return undefined;
  if (source.data?.op !== "multiply") return undefined;
  const left = getInputSourceNode(source.id, "left", ctx);
  const right = getInputSourceNode(source.id, "right", ctx);
  const groupedQuantity = getGroupedQuantityMultiplyAmountDescription(left, right, ctx) ?? getGroupedQuantityMultiplyAmountDescription(right, left, ctx);
  if (groupedQuantity) return groupedQuantity;
  const otherQuantity = getOtherQuantityMultiplyAmountDescription(left, right, ctx) ?? getOtherQuantityMultiplyAmountDescription(right, left, ctx);
  if (otherQuantity) return otherQuantity;
  const quantity = left?.type === "intQuantity" ? left : right?.type === "intQuantity" ? right : undefined;
  const kindCount = left?.type === "intKindCount" ? left : right?.type === "intKindCount" ? right : undefined;
  const countedSource = quantity ?? kindCount;
  const amountNode = countedSource === left ? right : left;
  const amount = amountNode ? describeValueOutput(amountNode, ctx) : undefined;
  if (!countedSource || amount === undefined) return undefined;
  const hasGlobalPrefix = isGlobalBoardTargetInput(countedSource, ctx);
  const target = normalizeGroupedQuantityTargetText(
    quantity ? describeQuantityTargets(quantity, ctx) : describeKindCountTargets(kindCount as PropDescriptionNode, ctx),
    { stripGlobalPrefix: hasGlobalPrefix },
  );
  const prefix = "";
  return {
    text: quantity ? describePerQuantityTargetText(prefix, target) : describePerKindCountTargetText(prefix, target),
    amount,
  };
}

function getQuantityMultiplyGrowAmountDescription(source: PropDescriptionNode, ctx: DescriptionContext): { text: string; amount: unknown } | undefined {
  if (source.type !== "intOperation" && source.type !== "floatOperation") return undefined;
  if (source.data?.op !== "multiply") return undefined;
  const left = getInputSourceNode(source.id, "left", ctx);
  const right = getInputSourceNode(source.id, "right", ctx);
  const groupedQuantity = getGroupedQuantityMultiplyAmountDescription(left, right, ctx) ?? getGroupedQuantityMultiplyAmountDescription(right, left, ctx);
  if (groupedQuantity) return groupedQuantity;
  const quantity = left?.type === "intQuantity" ? left : right?.type === "intQuantity" ? right : undefined;
  const kindCount = left?.type === "intKindCount" ? left : right?.type === "intKindCount" ? right : undefined;
  const countedSource = quantity ?? kindCount;
  const amountNode = countedSource === left ? right : left;
  const amount = amountNode ? describeValueOutput(amountNode, ctx) : undefined;
  if (!countedSource || amount === undefined) return undefined;
  const hasGlobalPrefix = isGlobalBoardTargetInput(countedSource, ctx);
  const target = normalizeGroupedQuantityTargetText(
    quantity ? describeQuantityTargets(quantity, ctx) : describeKindCountTargets(kindCount as PropDescriptionNode, ctx),
    { stripGlobalPrefix: hasGlobalPrefix },
  );
  return {
    text: quantity ? `每有1个${target}` : `每有1种${target}`,
    amount,
  };
}

function getGroupedQuantityMultiplyAmountDescription(
  groupedSource: PropDescriptionNode | undefined,
  amountSource: PropDescriptionNode | undefined,
  ctx: DescriptionContext,
): { text: string; amount: unknown } | undefined {
  const grouped = groupedSource ? getGroupedQuantityAmountDescription(groupedSource, ctx, { includeGlobalPrefix: true }) : "";
  const amount = amountSource ? describeValueOutput(amountSource, ctx) : undefined;
  if (!grouped || amount === undefined) return undefined;
  return { text: grouped, amount };
}

function getOtherQuantityMultiplyAmountDescription(
  quantityMinusOneSource: PropDescriptionNode | undefined,
  amountSource: PropDescriptionNode | undefined,
  ctx: DescriptionContext,
): { text: string; amount: unknown } | undefined {
  const quantity = getQuantityMinusOneSource(quantityMinusOneSource, ctx);
  const amount = amountSource ? describeValueOutput(amountSource, ctx) : undefined;
  if (!quantity || amount === undefined) return undefined;
  const hasGlobalPrefix = isGlobalBoardTargetInput(quantity, ctx);
  const target = normalizeGroupedQuantityTargetText(describeQuantityTargets(quantity, ctx), { stripGlobalPrefix: hasGlobalPrefix });
  return {
    text: describePerQuantityTargetText("", `其它${target}`),
    amount,
  };
}

function getQuantityMinusOneSource(source: PropDescriptionNode | undefined, ctx: DescriptionContext): PropDescriptionNode | undefined {
  if (!source || (source.type !== "intOperation" && source.type !== "floatOperation")) return undefined;
  if (source.data?.op !== "subtract") return undefined;
  const left = getInputSourceNode(source.id, "left", ctx);
  const right = getInputSourceNode(source.id, "right", ctx);
  if (left?.type !== "intQuantity") return undefined;
  return getNumericConstantValue(right) === 1 ? left : undefined;
}

function describePerQuantityTargetText(prefix: string, target: string) {
  return describePerCountedTargetText(prefix, target, "个");
}

function describePerKindCountTargetText(prefix: string, target: string) {
  return describePerCountedTargetText(prefix, target, "种");
}

function describePerCountedTargetText(prefix: string, target: string, unit: string) {
  if (prefix) return `${prefix}每有1${unit}${target}`;
  const positioned = describePositionedPerCountedTargetText(target, unit);
  return positioned || `每有1${unit}${target}`;
}

function describePositionedPerCountedTargetText(target: string, unit: string) {
  const labels = unique(objectValues(relativePositionLabels).concat(objectValues(absolutePositionLabels)))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  const label = labels.find((item) => target.startsWith(item) && target.length > item.length);
  if (!label) return "";
  const rest = target.slice(label.length);
  const suffix = ["对角线", "首行", "尾行", "首列", "尾列", "中间列"].indexOf(label) >= 0 ? "上" : "";
  return `${label}${suffix}每有1${unit}${rest}`;
}

function describeExtraYieldAmountEffect(amount: unknown) {
  const text = formatValue(amount);
  const range = parseSignedRange(text);
  if (range) return `魔力${range}`;
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return `魔力${numeric >= 0 ? "+" : "-"}${formatValue(Math.abs(numeric))}`;
  return `魔力+${text}`;
}

function parseSignedRange(text: string) {
  const match = text.match(/^(-?\d+(?:\.\d+)?)~(-?\d+(?:\.\d+)?)$/) ?? text.match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/);
  if (!match) return "";
  const min = Number(match[1]);
  const max = Number(match[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return "";
  if (min >= 0 && max >= 0) {
    const values = [min, max].sort((a, b) => a - b);
    return `+${formatValue(values[0])}~${formatValue(values[1])}`;
  }
  if (min <= 0 && max <= 0) {
    const values = [Math.abs(min), Math.abs(max)].sort((a, b) => a - b);
    return `-${formatValue(values[0])}~${formatValue(values[1])}`;
  }
  const values = [min, max].sort((a, b) => a - b);
  return `${formatSignedValue(values[0])}~${formatSignedValue(values[1])}`;
}

function describeExtraYieldMultiplierEffect(multiplier: unknown) {
  const numeric = Number(formatValue(multiplier));
  if (Number.isFinite(numeric)) return `魔力x${formatValue(numeric + 1)}`;
  return `魔力x(1+${formatValue(multiplier)})`;
}

function describeGrowAmountEffect(amount: unknown) {
  const text = formatValue(amount);
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return `魔力永久${numeric >= 0 ? "+" : "-"}${formatValue(Math.abs(numeric))}`;
  return `魔力永久+${text}`;
}

function describeGroupedQuantityGrowAction(nodeId: string, targetPrefix: string, conditionPrefix: string, ctx: DescriptionContext) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const randomGroupedTarget = describeRandomGroupedTargetGrowAction(nodeId, conditionPrefix, ctx);
  if (randomGroupedTarget) return randomGroupedTarget;
  const multiplied = amountSource ? getQuantityMultiplyGrowAmountDescription(amountSource, ctx) : undefined;
  if (multiplied) {
    const actionTargetPrefix = targetPrefix && !targetPrefix.startsWith("使") ? `使${targetPrefix}` : targetPrefix;
    return `${conditionPrefix}${multiplied.text}${actionTargetPrefix}${describeGrowAmountEffect(multiplied.amount)}`;
  }
  const grouped = amountSource ? getGroupedQuantityAmountDescription(amountSource, ctx, { includeGlobalPrefix: true }) : "";
  if (!grouped) return "";
  const actionTargetPrefix = targetPrefix && !targetPrefix.startsWith("使") ? `使${targetPrefix}` : targetPrefix;
  return `${conditionPrefix}${grouped}${actionTargetPrefix}${describeGrowAmountEffect(1)}`;
}

function describeRandomGroupedTargetGrowAction(nodeId: string, conditionPrefix: string, ctx: DescriptionContext) {
  const targetSource = getInputSourceNode(nodeId, "targets", ctx);
  if (targetSource?.type !== "filterRandom") return "";
  const countSource = getInputSourceNode(targetSource.id, "count", ctx);
  const grouped = countSource ? getGroupedQuantityAmountDescription(countSource, ctx, { includeGlobalPrefix: true }) : "";
  if (!grouped) return "";
  const amount = describeInputValue(nodeId, "amount", ctx, 1);
  const target = describeRandomFilterBaseTarget(targetSource, ctx);
  return target ? `${conditionPrefix}${grouped}，1个${target}${describeGrowAmountEffect(amount)}` : "";
}

function describeRandomFilterBaseTarget(node: PropDescriptionNode, ctx: DescriptionContext) {
  const targetSource = getInputSourceNode(node.id, "targets", ctx);
  const target = simplifyRandomFilterTargetText(targetSource ? describeValueOutput(targetSource, ctx) : "目标", ctx);
  return normalizeRandomFilterTargetScope(target, "").replace(/^随机/, "");
}

function describeGroupedQuantitySpawnAction(
  nodeId: string,
  conditionPrefix: string,
  positionPrefix: string,
  target: string,
  ctx: DescriptionContext,
) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const grouped = amountSource ? getGroupedQuantityAmountDescription(amountSource, ctx, { includeGlobalPrefix: true }) : "";
  if (!grouped) return "";
  return `${conditionPrefix}${grouped}${positionPrefix}生成1个${target}`;
}

function getGroupedQuantityAmountDescription(source: PropDescriptionNode, ctx: DescriptionContext, options: { includeGlobalPrefix: boolean }) {
  let operationSource = source;
  if (source.type === "roundNumber" && source.data?.mode === "floor") operationSource = getInputSourceNode(source.id, "value", ctx) ?? source;
  if (operationSource.type !== "intOperation" && operationSource.type !== "floatOperation") return "";
  if (operationSource.data?.op !== "divide") return "";
  const left = getInputSourceNode(operationSource.id, "left", ctx);
  const right = getInputSourceNode(operationSource.id, "right", ctx);
  if (left?.type !== "intQuantity") return "";
  const groupSize = getNumericConstantValue(right);
  if (!groupSize || groupSize <= 0) return "";
  const hasGlobalPrefix = options.includeGlobalPrefix && isGlobalBoardTargetInput(left, ctx);
  const target = normalizeGroupedQuantityTargetText(describeQuantityTargets(left, ctx), { stripGlobalPrefix: hasGlobalPrefix });
  const prefix = "";
  return `${prefix}每有${formatValue(groupSize)}个${target}`;
}

function normalizeGroupedQuantityTargetText(text: string, options: { stripGlobalPrefix?: boolean } = {}) {
  const normalized = (options.stripGlobalPrefix ? text.replace(/^场上/, "") : text).replace(/道具$/, "");
  return normalized === "盘面" || normalized === "盘面原料" ? "原料" : normalized;
}

function describeAccelerateTargetPrefix(target: string, nodeId?: string, ctx?: DescriptionContext) {
  if (target === "输入目标" || target === "当前道具") return "";
  if (target.startsWith("场上")) return target.slice(2);
  if (isRandomTargetText(target)) return target;
  if (nodeId && ctx && isGlobalPrototypeTargetInput(nodeId, ctx)) return target.replace(/^场上/, "");
  return target;
}

function formatPurifyTarget(target: string) {
  return target.replace(/^随机(\d+(?:\.\d+)?个)/, "$1").replace(/^随机(?!\d+(?:\.\d+)?个)/, "1个");
}

function formatSingleRandomActionTarget(target: string) {
  const text = target.trim();
  if (/^随机(?!\d+(?:\.\d+)?个)/.test(text)) return `1个${text.replace(/^随机/, "")}`;
  const onRandom = text.match(/^(.+?上的)随机(?!\d+(?:\.\d+)?个)(.+)$/);
  if (onRandom) return `1个${onRandom[1]}${onRandom[2]}`;
  const possessiveRandom = text.match(/^(.+?)的随机(?!\d+(?:\.\d+)?个)(.+)$/);
  if (possessiveRandom) return `1个${possessiveRandom[1]}${possessiveRandom[2]}`;
  return target;
}

function isGlobalPrototypeTargetInput(nodeId: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "targets", ctx);
  return source?.type === "filterPrototype" && isGlobalBoardTargetSource(source, ctx);
}

function describeInputTargets(nodeId: string, ctx: DescriptionContext) {
  return describeInputTargetList(nodeId, "targets", ctx, "输入目标", joinNaturalList, describeActionTargetValueOutput);
}

function describeInputSpawnTargets(nodeId: string, ctx: DescriptionContext) {
  return formatSpawnTargetText(describeInputTargetList(nodeId, "targets", ctx, "输入目标", joinNaturalOrList), ctx);
}

function formatSpawnTargetText(text: string, ctx: DescriptionContext) {
  const labels = unique(
    objectValues(typeLabels)
      .concat((ctx.options.tags ?? []).map((item) => String(item.label ?? "")))
      .filter(Boolean),
  ).sort((a, b) => b.length - a.length);
  return labels.reduce((result, label) => result.replace(new RegExp(`${escapeRegExp(label)}原料`, "g"), label), text);
}

function describeInputTarget(nodeId: string, portId: string, ctx: DescriptionContext, fallback = "当前道具") {
  if (portId === "targets") return describeInputTargetList(nodeId, portId, ctx, fallback);
  const binding = getFirstBinding(nodeId, portId, ctx);
  if (!binding) return fallback;
  const node = ctx.nodeMap.get(binding.source.nodeId);
  return node ? describeValueOutput(node, ctx) : fallback;
}

function describeInputTargetList(
  nodeId: string,
  portId: string,
  ctx: DescriptionContext,
  fallback: string,
  joiner: (values: string[]) => string = joinNaturalList,
  describeValue: (node: PropDescriptionNode, ctx: DescriptionContext) => string = describeValueOutput,
) {
  const values = getBindings(nodeId, portId, ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropDescriptionNode => Boolean(node))
    .map((node) => describeValue(node, ctx))
    .filter(Boolean);
  return values.length ? joiner(values) : fallback;
}

function describeActionTargetValueOutput(node: PropDescriptionNode, ctx: DescriptionContext) {
  if (node.type === "filterPrototype") return describePrototypeFilterOutput(node, ctx, joinNaturalList);
  return describeValueOutput(node, ctx);
}

function describeInputPrototypes(nodeId: string, ctx: DescriptionContext) {
  return describeInputPrototypeValues(nodeId, "prototypes", ctx, "目标-转化");
}

function describeInputPrototypeValues(
  nodeId: string,
  portId: string,
  ctx: DescriptionContext,
  fallback: string,
  joiner: (values: string[]) => string = joinNaturalOrList,
) {
  const values = getBindings(nodeId, portId, ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropDescriptionNode => Boolean(node))
    .map((node) => describeValueOutput(node, ctx))
    .filter(Boolean);
  return values.length ? joiner(values) : fallback;
}

function describeInputValue(nodeId: string, portId: string, ctx: DescriptionContext, fallback: unknown) {
  const binding = getFirstBinding(nodeId, portId, ctx);
  if (!binding) return fallback;
  const node = ctx.nodeMap.get(binding.source.nodeId);
  return node ? describeValueOutput(node, ctx) : fallback;
}

function describeOptionalInputValue(nodeId: string, portId: string, ctx: DescriptionContext) {
  const binding = getFirstBinding(nodeId, portId, ctx);
  if (!binding) return undefined;
  const node = ctx.nodeMap.get(binding.source.nodeId);
  return node ? describeValueOutput(node, ctx) : undefined;
}

function describeActionConditionPrefix(nodeId: string, ctx: DescriptionContext) {
  const sources = getBindings(nodeId, "condition", ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropDescriptionNode => Boolean(node));
  if (!sources.length) return "";
  const probabilityConditions = sources
    .filter((node) => node.type === "logicProbability")
    .map((node) => `${describeProbabilityValue(node.data?.probability)}概率`);
  const conditions = sources
    .filter((node) => node.type !== "logicProbability")
    .map((node) => describeSingleActionConditionPrefix(node, ctx))
    .filter(Boolean);
  const conditionText = describeActionConditionList(conditions);
  if (conditions.length && probabilityConditions.length) return `${conditionText}时${joinNaturalAndList(probabilityConditions)}`;
  if (probabilityConditions.length) return joinNaturalAndList(probabilityConditions);
  return conditionText ? `${conditionText}时` : "";
}

function describeActionConditionList(conditions: string[]) {
  if (conditions.length <= 1) return joinNaturalAndList(conditions);
  const existenceTargets = conditions.map((condition) => condition.match(/^有(.+)$/)?.[1] ?? "");
  if (existenceTargets.every(Boolean)) return `同时有${joinNaturalList(existenceTargets)}`;
  return joinNaturalAndList(conditions);
}

function describeSingleActionConditionPrefix(source: PropDescriptionNode, ctx: DescriptionContext) {
  if (source.type === "logicProbability") return `${describeProbabilityValue(source.data?.probability)}概率`;
  const natural = describeNaturalCondition(source, ctx);
  return natural || describeConditionPhrase(source, ctx);
}

function describeConditionPhrase(source: PropDescriptionNode, ctx: DescriptionContext) {
  const value = describeValueOutput(source, ctx);
  return value === "条件" ? "" : value;
}

function getFirstBinding(nodeId: string, portId: string, ctx: DescriptionContext) {
  return getBindings(nodeId, portId, ctx)[0];
}

function getBindings(nodeId: string, portId: string, ctx: DescriptionContext) {
  return ctx.bindingMap.get(`${nodeId}:${portId}`) ?? [];
}

function describeValueOutput(node: PropDescriptionNode, ctx: DescriptionContext): string {
  switch (node.type) {
    case "targetSelf":
      return "当前道具";
    case "targetBoard":
      return describeBoardMaterials(node);
    case "prototypeLibrary":
      return `${describePrototypeLibraryType(node)}库`;
    case "prototypeSelect":
      return describePrototype(String(node.data?.prototypeId ?? ""), ctx);
    case "targetFilter":
      return describeValueOutput(getInputSourceNode(node.id, "targets", ctx) ?? node, ctx);
    case "filterRarity":
      return describeRarityFilterOutput(node, ctx);
    case "filterTag":
      return describeTagFilterOutput(node, ctx);
    case "filterPrototype":
      return describePrototypeFilterOutput(node, ctx);
    case "filterRandom":
      return describeRandomFilterOutput(node, ctx);
    case "intConstant":
    case "floatConstant":
      return formatValue(node.data?.value ?? 0);
    case "intAttribute":
    case "floatAttribute":
      return `${describeInputTarget(node.id, "target", ctx)}${attributeLabels[String(node.data?.attribute)] ?? String(node.data?.attribute ?? "属性")}`;
    case "intQuantity":
      return `${describeQuantityTargets(node, ctx)}数量`;
    case "intKindCount":
      return `${describeKindCountTargets(node, ctx)}种类数`;
    case "intRandom":
    case "floatRandom":
      return describeRandomValueOutput(node.id, ctx);
    case "intOperation":
    case "floatOperation":
      return describeOperationValueOutput(node.id, String(node.data?.op ?? "add"), ctx);
    case "roundNumber":
      return describeRoundValueOutput(node.id, String(node.data?.mode ?? "round"), ctx);
    case "rarityConstant":
      return formatDictLabel(ctx.options.rarities, node.data?.value, `品质${node.data?.value ?? ""}`);
    case "tagConstant":
      return formatDictLabel(ctx.options.tags, node.data?.value, `标签${node.data?.value ?? ""}`);
    case "logicCompareInt":
    case "logicCompareFloat":
      return describeCompareNode(node.id, String(node.data?.op ?? "gte"), ctx);
    case "logicContains":
      return describeContainsNode(node.id, ctx);
    case "logicProbability":
      return `${describeProbabilityValue(node.data?.probability)}概率`;
    default:
      return "";
  }
}

function describeCompareNode(nodeId: string, op: string, ctx: DescriptionContext) {
  const natural = describeNaturalCondition(ctx.nodeMap.get(nodeId) as PropDescriptionNode, ctx);
  if (natural) return natural;
  const left = describeInputValue(nodeId, "left", ctx, "左值");
  const right = describeInputValue(nodeId, "right", ctx, "右值");
  return `${left}${compareLabels[op] ?? op}${right}`;
}

function describeRandomValueOutput(nodeId: string, ctx: DescriptionContext) {
  const min = describeInputValue(nodeId, "min", ctx, 0);
  const max = describeInputValue(nodeId, "max", ctx, 0);
  return `${formatValue(min)}~${formatValue(max)}`;
}

function describeOperationValueOutput(nodeId: string, op: string, ctx: DescriptionContext) {
  const left = describeInputValue(nodeId, "left", ctx, "左值");
  const right = describeInputValue(nodeId, "right", ctx, "右值");
  const symbol = operationLabels[op] ?? op;
  return `${left}${symbol}${right}`;
}

function describeRoundValueOutput(nodeId: string, mode: string, ctx: DescriptionContext) {
  const value = describeInputValue(nodeId, "value", ctx, "数值");
  return `${roundModeLabels[mode] ?? mode}(${value})`;
}

function describeContainsNode(nodeId: string, ctx: DescriptionContext) {
  const targetsSource = getInputSourceNode(nodeId, "targets", ctx);
  const targetSource = getInputSourceNode(nodeId, "target", ctx);
  const selfPositionCondition = describeSelfInAbsoluteBoardPositionCondition(targetsSource, targetSource, ctx);
  if (selfPositionCondition) return selfPositionCondition;
  const targets = describeInputTarget(nodeId, "targets", ctx, "目标");
  const target = describeInputTarget(nodeId, "target", ctx, "目标");
  return `${targets}包含${target}`;
}

function describeSelfInAbsoluteBoardPositionCondition(
  targetsSource: PropDescriptionNode | undefined,
  targetSource: PropDescriptionNode | undefined,
  ctx: DescriptionContext,
) {
  if (targetsSource?.type !== "targetBoard" || targetsSource.data?.scope !== "absolute") return "";
  const target = targetSource ? describeValueOutput(targetSource, ctx) : "";
  if (targetSource?.type !== "targetSelf" && target !== "当前道具") return "";
  const position = describeBoardLocationPrefix(targetsSource);
  return position ? `位于${position}` : "";
}

function describeNaturalCondition(node: PropDescriptionNode, ctx: DescriptionContext): string {
  if (node.type === "logicCompareInt" || node.type === "logicCompareFloat") return describeExistenceCondition(node, ctx);
  if (node.type === "logicContains") return describeContainsNode(node.id, ctx);
  return "";
}

function describeExistenceCondition(node: PropDescriptionNode, ctx: DescriptionContext) {
  const left = getInputSourceNode(node.id, "left", ctx);
  const right = getInputSourceNode(node.id, "right", ctx);
  const op = String(node.data?.op ?? "gte");
  const rightValue = getNumericConstantValue(right);
  if (left?.type === "intQuantity" && op === "eq" && rightValue === 0) {
    const target = normalizeGroupedQuantityTargetText(describeQuantityTargets(left, ctx), { stripGlobalPrefix: isGlobalBoardTargetInput(left, ctx) });
    if (target === "空位置") return "没有空位置";
  }
  if (left?.type === "intQuantity" && (op === "gte" || op === "gt") && rightValue !== undefined && rightValue <= 1) {
    return describeQuantityExistenceCondition(left, ctx);
  }
  if (left?.type === "intQuantity" && op === "gte" && rightValue !== undefined) {
    return `${describeQuantityTargets(left, ctx)}数量大于等于${formatValue(rightValue)}`;
  }
  return "";
}

function describeQuantityExistenceCondition(node: PropDescriptionNode, ctx: DescriptionContext) {
  const target = getInputSourceNode(node.id, "targets", ctx);
  if (target?.type === "filterTag") {
    const board = getInputSourceNode(target.id, "targets", ctx);
    const tag = describeInputConfiguredOrValues(target.id, "tag", ctx, "标签");
    if (board?.type === "targetBoard" && board.data?.scope === "relative") return formatExistenceConditionText(`${describeBoardLocationPrefix(board)}${tag}`);
    if (board?.type === "targetBoard" && board.data?.scope === "all") return `有${tag}`;
  }
  if (target?.type === "filterPrototype") return describeBoardPrototypeExistenceTarget(target, ctx);
  return formatExistenceConditionText(describeQuantityTargets(node, ctx));
}

function formatExistenceConditionText(target: string) {
  if (target.startsWith(relativePositionLabels.adjacent)) return `有${target}`;
  const positionLabels = [
    ...Object.values(relativePositionLabels),
    ...Object.values(absolutePositionLabels),
  ];
  if (/[和或]/.test(target) && positionLabels.some((label) => target.startsWith(label))) return `有${target}`;
  const position = positionLabels.find((label) => target.startsWith(label));
  if (!position) return `有${target}`;
  const rest = target.slice(position.length).replace(/^道具/, "");
  return `${position}有${rest || "道具"}`;
}

function getInputSourceNode(nodeId: string, portId: string, ctx: DescriptionContext) {
  const binding = getFirstBinding(nodeId, portId, ctx);
  return binding ? ctx.nodeMap.get(binding.source.nodeId) : undefined;
}

function getNumericConstantValue(node: PropDescriptionNode | undefined): number | undefined {
  if (!node) return undefined;
  if (node.type !== "intConstant" && node.type !== "floatConstant") return undefined;
  const value = Number(node.data?.value);
  return Number.isFinite(value) ? value : undefined;
}

function describeQuantityTargets(node: PropDescriptionNode, ctx: DescriptionContext) {
  return describeInputTarget(node.id, "targets", ctx, "目标");
}

function describeKindCountTargets(node: PropDescriptionNode, ctx: DescriptionContext) {
  return describeInputTarget(node.id, "targets", ctx, "目标");
}

function isGlobalBoardTargetInput(node: PropDescriptionNode, ctx: DescriptionContext): boolean {
  return isGlobalBoardTargetSource(getInputSourceNode(node.id, "targets", ctx), ctx);
}

function isGlobalBoardTargetSource(node: PropDescriptionNode | undefined, ctx: DescriptionContext): boolean {
  if (!node) return false;
  if (node.type === "targetBoard") return node.data?.scope === "all";
  return isGlobalBoardTargetSource(getInputSourceNode(node.id, "targets", ctx), ctx);
}

function describeBoardPrototypeExistenceTarget(node: PropDescriptionNode, ctx: DescriptionContext) {
  const board = getInputSourceNode(node.id, "targets", ctx);
  const prototype = describeInputPrototypeValues(node.id, "prototype", ctx, "指定原型");
  if (board?.type === "targetBoard" && board.data?.scope === "all") return `有${prototype}`;
  return formatExistenceConditionText(describeValueOutput(node, ctx));
}

function describeRarityFilterOutput(node: PropDescriptionNode, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  if (source?.type === "prototypeLibrary") {
    const rarity = describeInputConfiguredOrValues(node.id, "rarity", ctx, "品质");
    return `随机${rarity}${describePrototypeLibraryTypeSuffix(source)}`;
  }
  if (source?.type === "filterTag" && getInputSourceNode(source.id, "targets", ctx)?.type === "prototypeLibrary") {
    const rarity = describeInputConfiguredOrValues(node.id, "rarity", ctx, "品质");
    const tag = describeInputConfiguredOrValues(source.id, "tag", ctx, "标签");
    return `随机${rarity}${tag}`;
  }
  if (source?.type === "filterTag") {
    const boardSource = getInputSourceNode(source.id, "targets", ctx);
    if (boardSource?.type === "targetBoard") {
      const rarity = describeInputConfiguredOrValues(node.id, "rarity", ctx, "品质", joinNaturalList);
      const tag = describeInputConfiguredOrValues(source.id, "tag", ctx, "标签");
      const boardPrefix = describeBoardMaterialPrefix(boardSource);
      return boardPrefix ? `${boardPrefix}的${rarity}${tag}` : `${rarity}${tag}`;
    }
  }
  if (source?.type === "targetBoard") {
    const rarity = describeInputConfiguredOrValues(node.id, "rarity", ctx, "品质", joinNaturalList);
    const boardPrefix = describeBoardMaterialPrefix(source);
    return boardPrefix ? `${boardPrefix}的${rarity}原料` : `${rarity}原料`;
  }
  const target = describeInputTarget(node.id, "targets", ctx, "目标");
  const rarity = describeInputConfiguredOrValues(node.id, "rarity", ctx, "品质");
  return `${normalizeFilterTargetPrefix(target)}中品质为${rarity}的道具`;
}

function describeTagFilterOutput(node: PropDescriptionNode, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  const tag = describeInputConfiguredOrValues(node.id, "tag", ctx, "标签");
  if (source?.type === "prototypeLibrary") return `随机${tag}${describePrototypeLibraryTypeSuffix(source)}`;
  if (source?.type === "filterRarity" && getInputSourceNode(source.id, "targets", ctx)?.type === "prototypeLibrary") {
    const rarity = describeInputConfiguredOrValues(source.id, "rarity", ctx, "品质");
    return `随机${rarity}${tag}`;
  }
  if (source?.type === "targetBoard") {
    const boardPrefix = describeBoardMaterialPrefix(source);
    if (source.data?.scope === "all") return tag;
    return boardPrefix ? `${boardPrefix}${tag}` : `${tag}道具`;
  }
  return `${describeInputTargets(node.id, ctx)}中带有${tag}标签的道具`;
}

function describePrototypeFilterOutput(
  node: PropDescriptionNode,
  ctx: DescriptionContext,
  prototypeJoiner: (values: string[]) => string = joinNaturalOrList,
) {
  const target = describeInputTarget(node.id, "targets", ctx, "目标");
  const prototype = describeInputPrototypeValues(node.id, "prototype", ctx, "指定原型", prototypeJoiner);
  const source = getInputSourceNode(node.id, "targets", ctx);
  if (source?.type === "targetBoard") {
    const boardPrefix = describeBoardMaterialPrefix(source);
    if (source.data?.scope === "relative") return boardPrefix ? `${boardPrefix}${prototype}` : prototype;
    return boardPrefix ? `${boardPrefix}的${prototype}` : prototype;
  }
  if (target === "盘面原料") return prototype;
  return `${normalizeFilterTargetPrefix(target)}中原型为${prototype}的道具`;
}

function describeRandomFilterOutput(node: PropDescriptionNode, ctx: DescriptionContext) {
  const targetSource = getInputSourceNode(node.id, "targets", ctx);
  const target = simplifyRandomFilterTargetText(targetSource ? describeValueOutput(targetSource, ctx) : "目标", ctx);
  const count = describeInputValue(node.id, "count", ctx, node.data?.count ?? 1);
  const positionedTarget = describeSingleRandomPositionedTarget(targetSource, ctx);
  const scopePrefix = "";
  if (isSingleCount(count) && positionedTarget) return positionedTarget;
  if (targetSource?.type === "targetBoard" && targetSource.data?.scope === "all") {
    return isSingleCount(count) ? "随机原料" : `随机${formatValue(count)}个原料`;
  }
  const randomTarget = normalizeRandomFilterTargetScope(target, scopePrefix);
  if (isSingleCount(count) && isConfiguredTagLabel(randomTarget, ctx)) return `${scopePrefix}随机${randomTarget}`;
  if (isSingleCount(count)) return `${scopePrefix}随机${randomTarget}`;
  return `${scopePrefix}随机${formatValue(count)}个${randomTarget}`;
}

function describeSingleRandomPositionedTarget(source: PropDescriptionNode | undefined, ctx: DescriptionContext) {
  if (source?.type === "targetBoard" && source.data?.scope === "relative") {
    const boardPrefix = describeBoardMaterialPrefix(source);
    return boardPrefix ? `1个${boardPrefix}原料` : "";
  }
  if (source?.type !== "filterTag") return "";
  const boardSource = getInputSourceNode(source.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard") return "";
  const boardPrefix = describeBoardMaterialPrefix(boardSource);
  if (!boardPrefix) return "";
  const tag = describeInputConfiguredValues(source.id, "tag", ctx, "标签");
  return `1个${boardPrefix}${tag}`;
}

function simplifyRandomFilterTargetText(text: string, ctx: DescriptionContext) {
  const tagLabels = (ctx.options.tags ?? [])
    .map((item) => item.label)
    .filter((label): label is string => Boolean(label));
  const matchedGlobalTag = tagLabels.find((label) => text === `场上${label}` || text === `场上${label}道具` || text === `场上${label}原料`);
  if (matchedGlobalTag) return matchedGlobalTag;
  const matchedTag = tagLabels.find((label) => text === `${label}道具` || text === `${label}`);
  if (matchedTag) return matchedTag;
  const prototypeMatch = text.match(/^盘面(?:道具|原料)中原型为(.+)的(?:道具|原料)$/);
  if (prototypeMatch) return prototypeMatch[1];
  return text.replace(/道具$/, "");
}

function normalizeRandomFilterTargetScope(target: string, scopePrefix: string) {
  let result = target.trim().replace(/^随机/, "");
  if (scopePrefix && result.startsWith(scopePrefix)) result = result.slice(scopePrefix.length);
  return result.replace(/^随机/, "");
}

function isConfiguredTagLabel(text: string, ctx: DescriptionContext) {
  return (ctx.options.tags ?? []).some((item) => item.label === text);
}

function normalizeFilterTargetPrefix(target: string) {
  return target;
}

function describeInputConfiguredValues(nodeId: string, portId: "rarity" | "tag", ctx: DescriptionContext, fallback: string, separator = "、") {
  const values = getBindings(nodeId, portId, ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropDescriptionNode => Boolean(node))
    .map((node) => describeValueOutput(node, ctx))
    .filter(Boolean);
  return values.length ? values.join(separator) : fallback;
}

function describeInputConfiguredOrValues(
  nodeId: string,
  portId: "rarity" | "tag",
  ctx: DescriptionContext,
  fallback: string,
  joiner: (values: string[]) => string = joinNaturalOrList,
) {
  const values = getBindings(nodeId, portId, ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropDescriptionNode => Boolean(node))
    .map((node) => describeValueOutput(node, ctx))
    .filter(Boolean);
  return values.length ? joiner(values) : fallback;
}

function describeBoardMaterials(node: PropDescriptionNode) {
  const prefix = describeBoardMaterialPrefix(node);
  return prefix ? `${prefix}原料` : "原料";
}

function describeBoardLocationPrefix(node: PropDescriptionNode) {
  if (node.data?.scope === "relative") return joinNaturalOrList(getBoardPositionLabels(node.data.position, relativePositionLabels, "adjacent"));
  if (node.data?.scope === "absolute") return joinNaturalOrList(getBoardPositionLabels(node.data.position, absolutePositionLabels, "corner"));
  return "";
}

function describeBoardMaterialPrefix(node: PropDescriptionNode) {
  if (node.data?.scope === "relative") return joinNaturalList(getBoardPositionLabels(node.data.position, relativePositionLabels, "adjacent"));
  if (node.data?.scope === "absolute") return joinNaturalList(getBoardPositionLabels(node.data.position, absolutePositionLabels, "corner"));
  return "";
}

function getBoardPositionLabels(value: unknown, labels: Record<string, string>, fallback: string) {
  const list = Array.isArray(value) ? value : [value ?? fallback];
  return list.map((position) => labels[String(position)] ?? String(position)).filter(Boolean);
}

function describePrototypeLibraryType(node: PropDescriptionNode) {
  const type = Number(node.data?.propType ?? node.data?.type ?? 1);
  return typeLabels[type] ?? "原型";
}

function describePrototypeLibraryTypeSuffix(node: PropDescriptionNode) {
  const type = Number(node.data?.propType ?? node.data?.type ?? 1);
  return type === 1 ? "原料" : (typeLabels[type] ?? "道具");
}

function describePrototype(prototypeId: string, ctx: DescriptionContext) {
  if (!prototypeId) return "未设置原型";
  const props = ctx.options.props ?? [];
  const prop = props.find((item) => String(item._id) === prototypeId);
  return prop?.name || prop?.assetId || prototypeId;
}

function isImplicitEveryTurnText(text: string) {
  return text.indexOf("倒计时") >= 0 ||
    text.indexOf("额外产出") >= 0 ||
    isPermanentYieldText(text) ||
    isMagicYieldText(text) ||
    text.startsWith("减少") ||
    (/^[^，；]+减少/.test(text) && !text.startsWith("减少")) ||
    text.startsWith("消除") ||
    text.startsWith("保护") ||
    text.startsWith("免疫") ||
    text.startsWith("净化") ||
    text.startsWith("转化") ||
    text.startsWith("交换") ||
    text.startsWith("将") ||
    /^\d+(?:\.\d+)?个.+转化为/.test(text) ||
    text.startsWith("从") ||
    isRandomTargetActionText(text) ||
    /^位于.+时/.test(text) ||
    /^有.+时/.test(text) ||
    /^场上有.+时/.test(text);
}

function isRandomTargetActionText(text: string) {
  return text.startsWith("随机") || text.startsWith("场上随机") || text.indexOf("上的随机") >= 0 || text.indexOf("的随机") >= 0;
}

function isPermanentYieldText(text: string) {
  return /^魔力永久[+-]/.test(text) || /^.+魔力永久[+-]/.test(text);
}

function isDirectMagicYieldText(text: string) {
  return /^魔力(?:[+-]|x)/.test(text);
}

function isMagicYieldText(text: string) {
  return isDirectMagicYieldText(text) || /^.+魔力(?:[+-]|x)/.test(text);
}

function isPeriodicDirectActionText(text: string) {
  return (
    text.startsWith("使") ||
    text.startsWith("在") ||
    text.startsWith("生成") ||
    text.startsWith("消除") ||
    text.startsWith("永久增加") ||
    text.startsWith("永久减少") ||
    isPermanentYieldText(text) ||
    text.startsWith("减少") ||
    text.startsWith("额外产出") ||
    isMagicYieldText(text) ||
    text.indexOf("额外产出") >= 0 ||
    text.startsWith("净化") ||
    text.startsWith("倒计时") ||
    text.startsWith("随机") ||
    text.startsWith("场上随机") ||
    text.startsWith("场上每有") ||
    text.startsWith("每有") ||
    text.startsWith("保护") ||
    text.startsWith("免疫") ||
    text.startsWith("收集") ||
    text.startsWith("从") ||
    text.startsWith("获得") ||
    text.startsWith("当前委托") ||
    text.indexOf("抽取权重") >= 0 ||
    text.indexOf("抽取概率") >= 0 ||
    text.startsWith("将") ||
    text.startsWith("转化为")
  );
}

function isDelayedOneShotText(text: string) {
  return text.startsWith("转化为") ||
    text.startsWith("在") ||
    text.startsWith("生成") ||
    text.startsWith("消除") ||
    text.startsWith("永久增加") ||
    text.startsWith("永久减少") ||
    isPermanentYieldText(text) ||
    text.startsWith("减少") ||
    text.startsWith("额外产出") ||
    isMagicYieldText(text) ||
    text.indexOf("额外产出") >= 0 ||
    text.startsWith("净化") ||
    text.startsWith("保护") ||
    text.startsWith("免疫") ||
    text.startsWith("收集") ||
    text.startsWith("从") ||
    text.startsWith("获得") ||
    text.startsWith("当前委托") ||
    text.indexOf("抽取权重") >= 0 ||
    text.indexOf("抽取概率") >= 0 ||
    text.startsWith("将") ||
    text.startsWith("倒计时") ||
    text.startsWith("随机") ||
    text.startsWith("场上随机");
}

function describeProbabilityValue(value: unknown) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return `${formatValue(value)}`;
  return `${formatValue(numeric * 100)}%`;
}

function describeRarityWeightChange(multiplier: unknown) {
  const numeric = Number(formatValue(multiplier));
  return `x${Number.isFinite(numeric) ? formatValue(numeric) : formatValue(multiplier)}`;
}

function joinConditionedActionText(conditionPrefix: string, actionText: string) {
  const text = stripSelfActionSubject(actionText);
  if (!conditionPrefix) return text;
  if (isStandaloneProbabilityConditionText(conditionPrefix)) return formatProbabilityConditionedActionText(conditionPrefix, text);
  return `${conditionPrefix}${text.replace(/^使/, "")}`;
}

function formatProbabilityConditionedActionText(conditionPrefix: string, actionText: string) {
  const text = actionText.replace(/^使/, "");
  const target = getActionEffectTargetPrefix(text);
  if (!target) return `${conditionPrefix}${text}`;
  return `${target}${conditionPrefix}${text.slice(target.length)}`;
}

function getActionEffectTargetPrefix(text: string) {
  if (/^(魔力|倒计时|产出|额外产出|永久)/.test(text)) return "";
  const match = text.match(/^(.+?)(?=魔力|倒计时|产出|额外产出|永久|净化|不被消除)/);
  return match?.[1] ?? "";
}

function stripSelfActionSubject(text: string) {
  return text.replace(/(^|成功则|失败则|否则|并|再|使|概率)自身(?=魔力|倒计时|产出|额外产出|永久|净化)/g, "$1");
}

function formatSignedValue(value: unknown) {
  const text = formatValue(value);
  const numeric = Number(text);
  if (!Number.isFinite(numeric)) return text;
  return numeric >= 0 ? `+${formatValue(numeric)}` : `${formatValue(numeric)}`;
}

function formatCountedTarget(count: unknown, target: string) {
  if (isSingleCount(count)) return target.replace(/^随机/, "1个");
  return `${formatValue(count)}个${target.replace(/^随机/, "")}`;
}

function formatEliminateTarget(count: unknown, target: string) {
  const singleRandomTarget = normalizeSingleRandomEliminateTarget(target);
  if (count !== undefined) {
    if (isSingleCount(count) && /^\d+(?:\.\d+)?个/.test(target)) return target;
    if (isSingleCount(count) && /^\d+(?:\.\d+)?个/.test(singleRandomTarget)) return singleRandomTarget;
    if (isSingleCount(count)) return `1个${singleRandomTarget || target.replace(/^1个/, "")}`;
    return `${formatValue(count)}个${target.replace(/^随机/, "")}`;
  }
  if (/^\d+(?:\.\d+)?个/.test(singleRandomTarget)) return singleRandomTarget;
  return singleRandomTarget ? `1个${singleRandomTarget}` : target;
}

function normalizeSingleRandomEliminateTarget(target: string) {
  const text = target.trim();
  const onRandom = text.match(/^(.+?上的)随机(.+)$/);
  if (onRandom) return `${onRandom[1]}${onRandom[2]}`;
  const possessiveRandom = text.match(/^(.+?)的随机(.+)$/);
  if (possessiveRandom) return `${possessiveRandom[1]}${possessiveRandom[2]}`;
  const leadingRandom = text.match(/^随机(?:1个)?(.+)$/);
  if (leadingRandom) return leadingRandom[1];
  return "";
}

function isEliminateCountFromTargetQuantity(nodeId: string, ctx: DescriptionContext) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  if (amountSource?.type !== "intQuantity") return false;
  const amountTarget = getInputSourceNode(amountSource.id, "targets", ctx);
  const actionTarget = getInputSourceNode(nodeId, "targets", ctx);
  return Boolean(amountTarget && actionTarget && amountTarget.id === actionTarget.id);
}

function isSingleCount(value: unknown) {
  return Number(formatValue(value)) === 1;
}

function joinClause(parts: Array<string | undefined>) {
  return parts.filter(Boolean).join("，").replace(/，后，/g, "后，");
}

function joinNaturalList(values: string[]) {
  const list = unique(values.filter(Boolean));
  if (list.length <= 1) return list[0] ?? "";
  if (list.length === 2) return `${list[0]}和${list[1]}`;
  return `${list.slice(0, -1).join("、")}和${list[list.length - 1]}`;
}

function joinNaturalOrList(values: string[]) {
  const list = unique(values.filter(Boolean));
  if (list.length <= 1) return list[0] ?? "";
  if (list.length === 2) return `${list[0]}或${list[1]}`;
  return `${list.slice(0, -1).join("、")}或${list[list.length - 1]}`;
}

function joinNaturalAndList(values: string[]) {
  const list = unique(values.filter(Boolean));
  if (list.length <= 1) return list[0] ?? "";
  return list.join("且");
}

function ensureSentence(text: string) {
  const trimmed = text.trim();
  if (!trimmed) return "";
  return /[。！？]$/.test(trimmed) ? trimmed : `${trimmed}。`;
}

function formatValue(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
  }
  return String(value ?? "");
}

function formatDictLabel(options: PropDescriptionDictItem[] | undefined, value: unknown, fallback: string) {
  const item = options?.find((option) => String(option.value) === String(value));
  return item?.label || fallback;
}

function unique<T>(values: T[]) {
  return values.filter((value, index) => values.indexOf(value) === index);
}

function escapeRegExp(text: string) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function objectValues<T>(value: Record<string, T>) {
  return Object.keys(value).map((key) => value[key]);
}
